import { Stack, useMediaQuery, useTheme, Button, Theme } from '@mui/material';
import { JourneyViewSwitcher } from './detail/JourneyViewSwitcher';
import { JourneyFiltersButton } from './JourneyFiltersButton';
import { CreateButton } from './detail/CreateButton';
import { useModalContext } from '../modal/ModalContext';
import { detailLayoutSchema } from './JourneyDetailLayout';
import { FC } from 'react';
import { JourneyWarnings_GetJourneyQuery } from '../gql/graphql';
import { useTypedPathParams } from '../../../shared/fe/hooks/useTypedPathParams';
import { FormattedMessage } from 'react-intl';
import { FileDownloadOutlined, Link } from '@mui/icons-material';
import { AclAction, useAuthGuard } from '../utils/route-guard/useAuthGuard';
import { useLocation } from 'react-router';
import { useGeneratePDF } from '../hooks/useGeneratePDF';

type Props = Readonly<{ journey: JourneyWarnings_GetJourneyQuery }>;
export const JourneyHeaderActions: FC<Props> = ({ journey }) => {
	const theme = useTheme();
	const { setModal } = useModalContext();
	const { isActionAllowed } = useAuthGuard(AclAction.MANAGE_MEMBERS);
	const { id } = useTypedPathParams(detailLayoutSchema);
	const matchUpSM = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
	const location = useLocation();
	const { generatePdf } = useGeneratePDF();

	const isPlanningView = location.pathname.includes('planning');
	const isWarningsView = location.pathname.includes('warning');

	const openCreatePhaseModal = () => {
		setModal({ type: 'createPhase', payload: { id } });
	};

	const openCreateActivityModal = () => {
		setModal({ type: 'createActivity', payload: { journeyId: id } });
	};

	return (
		<Stack direction={'row'} alignItems={'stretch'} justifyContent={'space-between'}>
			<JourneyViewSwitcher warningsCount={journey.getJourney?.activityWarnings?.length ?? 0} />

			<Stack direction={'row'} gap={1.5} alignItems={'stretch'}>
				{matchUpSM && isActionAllowed && (
					<Button
						startIcon={<Link sx={{ mt: 0.2 }} />}
						variant={'text'}
						sx={{
							color: theme.palette.text.secondary,
							'&:hover': { background: 'none', color: 'inherit' },
						}}
						onClick={() => setModal({ type: 'inviteMembers' })}
					>
						<FormattedMessage id={'journey.view.header.share'} />
					</Button>
				)}
				{!isPlanningView && !isWarningsView && (
					<Button
						variant={'text'}
						sx={{
							color: theme.palette.text.secondary,
							'&:hover': { background: 'none', color: 'inherit' },
						}}
						startIcon={<FileDownloadOutlined />}
						onClick={async () => await generatePdf()}
					>
						<FormattedMessage id={'journey.view.header.export'} />
					</Button>
				)}
				{!isPlanningView && (
					<>
						<JourneyFiltersButton />
						<CreateButton
							onPhaseCreate={openCreatePhaseModal}
							onActivityCreate={openCreateActivityModal}
							disableActivityCreation={journey.getJourney?.phases.length === 0}
						/>
					</>
				)}
			</Stack>
		</Stack>
	);
};
